<template>
    <tbody>
        <tr class="new-row-animation">
            <td class="tx-mobile-flex-content" colspan="10">
                <ui-link v-if="!hideIcon" class="tx-table-cell-icon"
                    v-bind:to="transactionRoute"
                    v-bind:title="!is_success ? $t('tx.error_icon_tooltip', [exitCode]) : undefined"
                    v-bind:class="{
                        'tx-table-cell-icon--error': is_bounced && !unsuccessfulBid && finality !== 'pending',
                        'tx-table-cell-icon--in': !isOut && !isFailed && !isUnknown && finality !== 'pending',
                        'tx-table-cell-icon--out': isOut && !isFailed && !isUnknown && finality !== 'pending',
                        'tx-table-cell-icon--service': isUnknown && !isFailed && finality !== 'pending',
                        'tx-table-cell-icon--blue': (is_swapped && is_success && finality !== 'pending') || [
                            'subscribe',
                            'unsubscribe',
                            'jetton_mint',
                            'deploy_nft',
                            'nft_purchase',
                        ].includes(event),
                        'tx-table-cell-icon--gray': finality === 'pending',
                        'tx-table-cell-icon--failed': isFailed && finality !== 'pending',
                    }">

                    <icon-tx-bounced v-if="is_bounced"/>

                    <!-- Transaction finality -->
                    <icon-tx-pending v-once v-else-if="finality === 'pending'" class="tx-pending-icon" />

                    <!-- <icon-tx-confirmed v-once v-else-if="finality === 'confirmed'" /> -->

                    <icon-tx-error v-else-if="!is_success"/>

                    <action-icon-mobile
                        v-else-if="action || event"
                        v-bind:actionName="action?.type"
                        v-bind:eventName="event"
                        v-bind:amount="amount"
                        v-bind:isOut="isOut"/>

                    <template v-else>
                        <icon-out v-if="isOut"/>
                        <icon-in v-else/>
                    </template>
                </ui-link>

                <div v-else class="tx-table-cell-icon"
                    v-bind:to="transactionRoute"
                    v-bind:title="!is_success ? $t('tx.error_icon_tooltip', [exitCode]) : undefined"
                    v-bind:class="{
                        'tx-table-cell-icon--error': is_bounced,
                        'tx-table-cell-icon--in': !isOut && !isFailed && !isUnknown,
                        'tx-table-cell-icon--out': isOut && !isFailed && !isUnknown,
                        'tx-table-cell-icon--service': isUnknown && !isFailed,
                        'tx-table-cell-icon--blue': (is_swapped && is_success) || [
                            'subscribe',
                            'unsubscribe',
                            'jetton_mint',
                            'deploy_nft',
                        ].includes(event),
                        'tx-table-cell-icon--failed': isFailed,
                    }">

                    <icon-tx-bounced v-if="is_bounced"/>

                    <icon-tx-error v-else-if="!is_success"/>

                    <action-icon-mobile
                        v-else-if="action || event"
                        v-bind:actionName="action?.type"
                        v-bind:eventName="event"
                        v-bind:amount="amount"
                        v-bind:isOut="isOut"/>

                    <template v-else>
                        <icon-out v-if="isOut"/>
                        <icon-in v-else/>
                    </template>
                </div>

                <div class="tx-mobile-content">
                    <div class="tx-mobile-content__top">
                        <!-- Top row: date and amount: -->
                        <div class="tx-mobile-content-row">
                            <!-- Date is on the left: -->
                            <span v-if="finality === 'pending'" class="tx-mobile-content__time muted">
                                {{ $t('address.tx_table.pending') }}
                            </span>
                            <!-- <span v-else-if="finality === 'confirmed'" class="tx-mobile-content__time muted">
                                {{ $t('address.tx_table.confirmed') }}
                            </span> -->
                            <ui-timeago v-else class="tx-mobile-content__time muted" v-bind:timestamp="timestamp"/>

                            <!-- Action description or amount is on the right: -->
                            <template>
                                <!-- Service actions.
                                     A row that carries an action still has a
                                     value to show, whoever it was addressed to:
                                     the placeholder is for rows that have none. -->
                                <!-- Nothing is known about the value of an unrecognized operation,
                                     its place is taken by a shortcut to the filter that hides them: -->
                                <button v-if="isUnknown" class="tx-hide-unknown"
                                    v-on:click="hideUnknown"
                                    v-text="$t('common.hide')"/>

                                <div v-else-if="(is_service || is_external || isLog) && !action" class="muted">
                                    {{$t(`common.empty`)}}
                                </div>

                                <!-- tonscan api action badge: -->
                                <action-badge-mobile
                                    v-else-if="action?.type && event !== 'nft_purchase' && event !== 'nft_transfer' && event !== 'deploy_nft'"
                                    v-bind:action="action"
                                    v-bind:amount="amount"
                                    v-bind:is_out="isOut"
                                    v-bind:is_failed="isFailed"
                                    v-bind:from="from"
                                    v-bind:meta="meta"
                                    v-bind:to="to"
                                    v-bind:event="event"/>

                                <div v-else-if="event === 'nft_purchase'" v-bind:class="{
                                    'tx-amount--in': isOut && !isFailed,
                                    'tx-amount--out': !isOut && !isFailed,
                                    'muted': isFailed,
                                }">
                                    {{$ton(amount)}} GRAM
                                </div>

                                <div v-else-if="event === 'nft_transfer' || event === 'deploy_nft'">
                                    1 NFT
                                </div>

                                <div v-else-if="[
                                         'jetton_burn',
                                         'jetton_mint',
                                         'auction_bid',
                                     ].includes(event)"
                                    v-bind:class="{
                                        'tx-amount--in': !isOut && !isFailed,
                                        'tx-amount--out': isOut && !isFailed,
                                        'muted': isFailed,
                                    }">
                                    {{$ton(amount, meta?.decimals ?? 9)}} {{displayTicker(meta?.jetton?.symbol || meta?.symbol, meta?.jetton_address)}}
                                </div>

                                <!-- Events without TON amount have no value: their name goes
                                     to the bottom row, like the name of any other action.
                                     A failed one keeps it here, the reason takes the bottom: -->
                                <div class="muted" v-else-if="isValueless">
                                    <template v-if="isFailed">{{$t(`events.${event}`)}}</template>
                                </div>

                                <template v-else-if="event && event.endsWith('_dns') && meta?.domain">
                                    <ui-link
                                        style="margin-left: 5px;"
                                        v-bind:to="{ name: 'address', params: {
                                            address: to,
                                        }}">
                                        {{truncateDomain(meta.domain)}}
                                    </ui-link>
                                </template>

                                <!-- TON transfers. A failed one moved nothing,
                                     so it carries neither sign nor colour: -->
                                <div v-else v-bind:class="{
                                    'tx-amount--in': !isOut && !isFailed,
                                    'tx-amount--out': isOut && !isFailed,
                                    'muted': isFailed,
                                }">{{$ton(amount)}} GRAM</div> <!-- No line breaks to avoid whitespaces -->
                            </template>
                        </div>

                        <!-- Bottom row: address and action: -->
                        <div class="tx-mobile-content-row">
                            <!-- Transaction account is on the left: -->
                            <template>
                                <span v-if="is_external || event === 'external'" class="muted">
                                    {{$t('address.tx_table.external')}}
                                </span>

                                <ui-link v-else-if="is_aggregated" v-bind:to="transactionRoute">
                                    {{$tc('address.tx_table.address_count', outputCount)}}
                                </ui-link>

                                <span v-else-if="isUnknown" class="tx-mobile-unknown-label" v-text="$t('events.unknown')"/>

                                <template v-else-if="isLog">
                                    {{$t('address.tx_table.nowhere')}}
                                </template>

                                <template v-else-if="event === 'nft_purchase'">
                                    <ui-address v-if="isOut" v-bind:address="receiver"
                                        v-bind:type="destination_type"
                                        v-bind:alias="destination_alias"
                                        style="max-width: 230px;"
                                        v-bind:disabled="address === receiver"
                                    />
                                    <template v-else>
                                        <ui-address v-if="meta?.marketplace && meta.marketplace === 'fragment'" address="UQBAjaOyi2wGWlk-EDkSabqqnF-MrrwMadnwqrurKpkla4QB" />
                                        <span style="text-transform: capitalize" v-else-if="meta?.marketplace">
                                            {{ meta.marketplace }}
                                        </span>
                                        <ui-address v-else v-bind:address="sender"
                                            v-bind:type="source_alias"
                                            v-bind:alias="source_alias"
                                            style="max-width: 230px;"
                                        />
                                    </template>
                                </template>

                                <ui-address v-else-if="event === 'deploy_nft'"
                                    v-bind:address="action.nft"
                                />

                                <ui-address v-else-if="!isOut"
                                    v-bind:address="sender"
                                    v-bind:type="source_type"
                                    v-bind:alias="$store.getters.getLocalAddressbookName(sender) || source_alias"/>

                                <ui-address v-else
                                    v-bind:address="receiver"
                                    v-bind:type="destination_type"
                                    v-bind:alias="$store.getters.getLocalAddressbookName(receiver) || destination_alias"/>
                            </template>

                            <!-- Transaction action is on the right: -->
                            <div class="muted" style="padding-left: 12px">
                                <!-- tonscan api actions: -->
                                <template v-if="action">
                                    <span v-if="action?.type && action.type.endsWith(':excesses')">
                                        Excess
                                    </span>

                                    <span v-else-if="event === 'nft_purchase'">
                                        <template v-if="isOut">{{$t('events.nft_sold')}}</template>
                                        <template v-else>{{$t('events.nft_purchase')}}</template>
                                    </span>

                                    <span v-else-if="event === 'deploy_nft'">
                                        {{$t('events.deploy_nft')}}
                                    </span>

                                    <span v-else-if="event.startsWith('jvault')">
                                        {{ $t(`events.${event}`) }}
                                    </span>

                                    <span v-else-if="event.startsWith('evaa')">
                                        {{ $t(`events.${event}`) }}
                                    </span>

                                    <span v-else-if="action?.type === 'nft:transfer_action'">
                                        <template v-if="event === 'auction_bid'">
                                            {{ $t('nft.item.badge.bid') }}
                                        </template>
                                        <template v-else-if="event === 'teleitem_start_auction'">
                                            {{ $t(`events.teleitem_start_auction`) }}
                                        </template>
                                        <template v-else>
                                            <template v-if="isOut">{{$t('events.sent_nft')}}</template>
                                            <template v-else>{{$t('events.received_nft')}}</template>
                                        </template>
                                    </span>

                                    <!-- dex liquidity actions: -->
                                    <template v-else-if="action?.type && action.type.startsWith('dex:')">
                                        {{$t(`events.${event}`)}}
                                    </template>

                                    <!-- nominator pool actions: -->
                                    <template v-else-if="action?.type && action.type.startsWith('pool:')">
                                        <template v-if="action.type === 'pool:recover_stake'">
                                            Recover stake
                                        </template>

                                        <template v-else-if="action.type === 'pool:recover_stake_ok'">
                                            Stake recovered
                                        </template>

                                        <template v-else-if="action.type === 'pool:new_stake'">
                                            <template v-if="amount > 5000000000">
                                                Send stake
                                            </template>
                                            <template v-else>
                                                Push stake
                                            </template>
                                        </template>

                                        <template v-else-if="action.type === 'pool:update_validator_set_hash'">
                                            Update validator
                                        </template>

                                        <template v-else-if="action.type === 'pool:process_withdraw_requests'">
                                            Process withdraws
                                        </template>
                                    </template>

                                    <!-- jetton actions: -->
                                    <template
                                        v-else-if="action?.type && [
                                            'jetton:burn',
                                            'jetton:transfer',
                                            'jetton:transfer_notification',
                                            'jetton:internal_transfer',
                                            'jetton:swap',
                                            'jetton:route_tonapi',
                                        ].includes(action.type)">

                                        <span
                                            v-if="action.type === 'jetton:burn'">
                                            {{$t('events.jetton_burn')}}
                                        </span>

                                        <!-- A failed swap keeps its signs, so the two legs
                                             can be told apart, but loses the colours: -->
                                        <div v-else-if="action.type === 'jetton:swap'" class="mobile-swap"
                                            v-bind:class="{ 'mobile-swap--green': !isFailed }">
                                            <span v-if="meta.jetton_out_address !== null" class="ui-inline-jetton__link"
                                                v-bind:class="{ 'ui-inline-jetton--green': !isFailed }"
                                                v-bind:to="{ name: 'token', params: { address: meta.jetton_out_address }}">
                                                <ui-inline-jetton
                                                    v-bind:isOut="false"
                                                    v-bind:address="meta.jetton_out_address"
                                                    v-bind:value="meta.amount_out"
                                                    v-bind:symbol="meta.jetton_out_address === 'EQCM3B12QK1e4yZSf8GtBRT0aLMNyEsBc_DhVfRRtOEffLez' ? 'GRAM' : (meta.symbol_out || meta.jetton_out_address)"
                                                    v-bind:decimals="meta.decimals_out"
                                                    v-bind:hide-color="isFailed"
                                                    show-sign
                                                />
                                            </span>
                                            <span v-bind:style="isFailed ? null : 'color: var(--green-bright)'" v-else>
                                                &plus;{{$ton(meta.amount_out, meta.decimals_out, true)}} GRAM
                                            </span>
                                        </div>

                                        <span
                                            v-else-if="action.type === 'jetton:transfer'">
                                            {{$t('events.transferred')}}
                                        </span>

                                        <template v-else>
                                            <span v-if="isOut">
                                                {{$t('events.transferred')}}
                                            </span>
                                            <span v-else>
                                                {{$t('events.received')}}
                                            </span>
                                        </template>
                                    </template>
                                </template>

                                <!-- A failed action has nothing to price and its kind
                                     says little, the reason it failed goes in its place: -->
                                <span v-else-if="isFailed" style="margin-left: 10px;">
                                    <template v-if="exitCode">{{$t('tx.exit_code_error', [exitCode])}}</template>
                                    <template v-else>{{$t('tx.status_failed')}}</template>
                                </span>

                                <!-- Other actions. An external message is already
                                     named on the left, so it is not repeated here: -->
                                <template
                                    v-else-if="[
                                        'subscribe',
                                        'stake_deposit',
                                        'election_recover',
                                        'election_deposit',
                                        'call_contract',
                                        'stake_withdrawal',
                                        'jetton_mint',
                                        'auction_bid',
                                        'jetton_route',
                                        'dex_withdraw_liquidity',
                                        'dex_deposit_liquidity',
                                        'jvault_stake',
                                        'jvault_unstake',
                                        'jvault_claim',
                                        'vesting_send_message',
                                        'evaa_supply',
                                        'evaa_withdraw',
                                        'evaa_liquidate',
                                        'gasless_request',
                                    ].includes(event) ||
                                        (isValueless && event !== 'external') ||
                                        (event === 'contract_deploy' && amount) ||
                                        (event && event.endsWith('_dns') && meta?.domain)
                                    ">
                                    {{$t(`events.${event}`)}}
                                </template>

                                <template v-else-if="showFiatValue">
                                    <span style="margin-left: 10px;">≈ <ui-fiat v-bind:tonValue="amount"/></span>
                                </template>

                                <tx-gasless-icon v-if="gasless_parent" style="margin-left: 2px;" />
                            </div>
                        </div>
                    </div>

                    <div v-bind:class="isOut ? 'big-nft-container' : ''">
                        <ui-inline-nft-item
                            v-if="action && action?.type === 'nft:transfer_action'"
                            v-bind:address="action.nft"
                            v-bind:event-type="event"
                            v-bind:image-src="meta.image"
                            v-bind:nft-name="meta.name"
                            v-bind:is-out="isOut"
                            v-bind:collection-name="meta.collection"
                            v-bind:collection-address="meta?.collectionAddress"
                        />
                    </div>

                    <div v-if="message || encrypted || op === '0x2167da4b'"
                        class="tx-mobile-content__bottom"
                        v-bind:class="{'tx-mobile-content__bottom--right': isOut}">
                        <!-- Encrypted message: -->
                        <template v-if="encrypted || op === '0x2167da4b'">
                            <div class="tx-mobile-comment"
                                v-bind:class="{'tx-mobile-comment--right': isOut}">
                                <span class="tx-mobile-comment__contents muted" v-text="$t('tx.encrypted_message')"/>
                            </div>
                        </template>

                        <!-- Transaction comment: -->
                        <template v-else-if="message">
                            <div class="tx-mobile-comment"
                                v-bind:class="{'tx-mobile-comment--right': isOut}"
                                v-on:click="showMore">
                                <span class="tx-mobile-comment__contents"
                                    v-bind:class="{ 'tx-mobile-comment__contents--truncated': isTruncated }"
                                    v-text="message"/>

                                <button class="tx-mobile-comment__expander"
                                    v-if="isTruncated"
                                    v-text="$t('address.tx_table.show_more')"/>
                            </div>
                        </template>
                    </div>
                </div>
            </td>
        </tr>
    </tbody>
</template>

<script>
import { isSameAddress } from '~/tonweb.js';
import IconTxError from '@img/icons/lucide/x.svg?vue';
import IconTxBounced from '@img/icons/lucide/undo-2.svg?vue';
import IconIn from '@img/icons/lucide/arrow-down-left.svg?vue';
import IconOut from '@img/icons/lucide/arrow-up-right.svg?vue';
import IconTxPending from '@img/icons/tonscan/round-loader.svg?vue';
// import IconTxConfirmed from '@img/icons/lucide/check-check.svg?vue';
import { displayTicker } from '~/helpers';
import UiInlineJetton from '~/components/address/TxRowInlineJetton.vue';
import { base64ToHex } from '~/utils';
import UiInlineNftItem from './TxRowInlineNft.vue';
import ActionIconMobile from './TxRowActionIconMobile.vue';
import ActionBadgeMobile from './TxRowActionBadgeMobile.vue';
import TxGaslessIcon from './TxGaslessIcon.vue';

export default {
    props: {
        address: String,
        date: String,
        from: String,
        is_service: Boolean,
        is_external: Boolean,
        is_aggregated: Boolean,
        is_success: Boolean,
        is_bounced: Boolean,
        is_swapped: Boolean,
        outputCount: Number,
        to: String,
        amount: String,
        message: String,
        timestamp: Number,
        txHash: String,
        exitCode: Number,
        op: [Number, String],
        source_type: String,
        source_alias: String,
        destination_type: String,
        destination_alias: String,
        action: Object,
        event: String,
        source: String,

        meta: {
            type: Object,
            default: () => ({}),
        },

        messageMaxChars: {
            type: Number,
            default: 200,
        },
        hideIcon: Boolean,
        encrypted: Boolean,
        gasless_parent: String,

        finality: {
            type: String,
            default: () => 'finalized',
        },
    },

    data() {
        return {
            isTruncated: false,
        };
    },

    computed: {
        showFiatValue() {
            return !this.action
                && !this.is_service
                && !this.is_external
                && !this.isLog
                && this.is_success
                && this.amount > 0
                && this.event !== 'withdraw_stake_request';
        },

        sender() {
            switch (this.action?.type) {
                case 'jetton:transfer_notification': {
                    return this.action.sender;
                }

                case 'nft:ownership_assigned': {
                    return this.action.prev_owner;
                }

                default: {
                    return this.from;
                }
            }
        },

        receiver() {
            switch (this.action?.type) {
                case 'jetton:transfer': {
                    return this.action.destination;
                }

                case 'nft:transfer': {
                    return this.action.new_owner;
                }

                default: {
                    return this.to;
                }
            }
        },

        isLog() {
            return !this.to;
        },

        isValueless() {
            return [
                'unsubscribe',
                'external',
                'stake_withdrawal_request',
                'multisig_execute',
                'multisig_create_order',
                'multisig_approve',
                'vesting_add_whitelist',
                'teleitem_cancel_auction',
                'change_wallet_key',
            ].includes(this.event)
                || (this.event === 'contract_deploy' && !this.amount)
                || (this.event?.endsWith('_dns') && !this.meta?.domain);
        },

        // Toncenter could not classify the trace, so it has no direction or counterparty:
        isUnknown() {
            return this.event === 'unknown';
        },

        isOut() {
            // On a jetton wallet page the address is the wallet, and the sender is its owner:
            return this.event === 'sent_jetton' || isSameAddress(this.from, this.address);
        },

        transactionRoute() {
            return {
                name: 'tx',
                params: {
                    hash: base64ToHex(this.txHash),
                },
            };
        },

        unsuccessfulBid() {
            return !this.is_success && this.event === 'auction_bid';
        },

        // A bounced transaction is drawn in its own colour, so it is not
        // counted as a plain failure here:
        isFailed() {
            return !this.is_success && !this.is_bounced;
        },
    },

    created() {
        this.isTruncated = this.message && (this.message.length > this.messageMaxChars);
    },

    methods: {
        displayTicker,
        showMore() {
            this.isTruncated = false;
        },

        // Unknown operations belong to the «Contract calls» filter group:
        hideUnknown() {
            if (!this.$store.state.excludeGroups.includes('contractCall')) {
                this.$store.commit('toggleExcludeActionGroup', 'contractCall');
            }
        },

        truncateDomain(domain) {
            if (domain && domain.length > 20) {
                return domain.slice(0, 8) + '...' + domain.slice(-8);
            }
            return domain;
        },
    },

    components: {
        UiInlineNftItem,
        UiInlineJetton,
        IconTxError,
        IconTxBounced,
        IconTxPending,
        IconIn,
        IconOut,
        // IconTxConfirmed,
        ActionIconMobile,
        ActionBadgeMobile,
        TxGaslessIcon,
    },
};
</script>
